/**
 * @format
 */

import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import * as routes from '../../routes';
import * as availabilitySearchActions from '../../actions/availabilitySearchActions';
import * as appointmentActions from '../../actions/appointmentActions';
import * as rd2RefactorActions from '../../actions/rd2RefactorActions';
import CancelAppointmentDetailsOverview from '../../components/appointment/cancelAppointmentDetailsOverview';
import { validateField } from '../../lib/validation';
import { ConfirmationModal } from '../../components/common/standardComponents';
import LoadingIndicator from '../../components/loadingIndicator';
import { PATIENT_DETAILS, APPOINTMENT_DETAILS } from 'constants/actionReferrer';
import { withRouter } from 'components/hoc';
import { useAppDispatch, useAppSelector } from '../../store';
import { useParams } from 'react-router-dom';

const AppointmentCancelView: React.FC = () => {
    const dispatch = useAppDispatch();

    const activeCareOrder = useAppSelector((state) => state.careOrder);
    const activePatient = useAppSelector((state) => state.activePatient);
    const availabilitySearchConfig = useAppSelector((state) => state.config.availabilitySearch);
    const emailCancellationEnabled = useAppSelector((state) => state.config.notification.emailCancellationEnabled);
    const enableConfirmationEmailOptIn = useAppSelector((state) => state.config.notification.enableConfirmationEmailOptIn);
    const decisionSupport = useAppSelector((state) => state.config.decisionSupport);
    const decisionSupportOutput = useAppSelector((state) => state.decisionSupport);
    const enableCancellationEmailOptIn = useAppSelector((state) => state.config.notification.showPatientCancellationEmailOptIn);
    const notificationConfig = useAppSelector((state) => state.config.notification);
    const patientEmail = useAppSelector((state) => state.activePatient.details.email);
    const patientReferenceId = useAppSelector((state) => state.activePatient.details.referenceId);
    const patientCancellationEmailDisclaimer = useAppSelector((state) => state.config.notification.patientCancellationEmailDisclaimer);
    const previousAppointmentReferenceId = useAppSelector((state) => state.appointment.cancelRescheduleInfo.previousAppointmentReferenceId);
    const providerFieldConfig = useAppSelector((state) => state.config.provider);
    const referralSiteId = useAppSelector((state) => state.auth.referralSiteId);
    const referralSystemId = useAppSelector((state) => state.auth.referralSystemId);
    const cancelRescheduleInfo = useAppSelector((state) => state.appointment.cancelRescheduleInfo);
    const schedulingConfig = useAppSelector((state) => state.config.scheduling);
    const systemCancelReasons = useAppSelector((state) => state.config.systemCancelReasons);
    const token = useAppSelector((state) => state.auth.token);
    const useDecisionSupport = useAppSelector((state) => state.config.decisionSupport.useDecisionSupport);
    const routePrefix = useAppSelector((state) => state.config.instance.routePrefix);

    const params = useParams();
    
    const [collapse, setCollapse] = useState({
        emailConsent: false,
        email: false
    });
    const [appointmentDetails, setAppointmentDetails] = useState({});
    const [error, setError] = useState<string>('');
    const [fieldValidation, setFieldValidation] = useState({
        cancellationEmailOther: { dataType: 'Email', valid: true, required: false },
        reasonForCancellation: {
            dataType: 'String',
            valid: true,
            max: 200,
            required: schedulingConfig.showReasonForCancellation,
        }
    });
    const [isPageValid, setIsPageValid] = useState<boolean>(true);
    const [isAppointmentCanceled, setIsAppointmentCanceled] = useState<boolean>(false);
    const [isCanceling, setIsCanceling] = useState<boolean>(false);
    const [isCancelMode, setIsCancelMode] = useState<boolean>(true);
    const [isDetailsLoading, setIsDetailsLoading] = useState<boolean>(true);
    const [reasonForCancellation, setReasonForCancellation] = useState<string>('');
    const [cancellationEmailConsent, setCancellationEmailConsent] = useState<boolean>(false);
    const [cancellationEmail, setCancellationEmail] = useState<string>('');
    const [cancellationEmailOther, setCancellationEmailOther] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>('EN');

    useEffect(() => {
        const referenceId = params?.referenceId;
        loadAppointmentDetails(referenceId);
        initializePatientNotifications();
    }, [params.referenceId])

    const loadAppointmentDetails = async (referenceId: any) => {
        const response = await dispatch(appointmentActions.getPreviousAppointmentByReferenceId(referenceId));
        if (response.error) {
            setError('Appointment not found and has been cancelled already.');
            setIsDetailsLoading(false);
        } else {
            let appointmentDetails = response.payload.data;
            setAppointmentDetails({ ...appointmentDetails });
            setIsDetailsLoading(false);
        }
    };

    const initializePatientNotifications = () => {
        if (emailCancellationEnabled && !enableCancellationEmailOptIn) {
            togglePatientNotifications('emailConsent');

            setCancellationEmailConsent(true);

            let updatedFieldValidation = { ...fieldValidation };
            updatedFieldValidation.cancellationEmailOther.valid =
                patientEmail && patientEmail.trim() !== '' ? true : false;
            updatedFieldValidation.cancellationEmailOther.required =
                patientEmail && patientEmail.trim() !== '' ? false : true;
            setFieldValidation(updatedFieldValidation);
        }
    };

    const handleCheckboxChange = (event: any, requiredFields: any[]) => {
        const { checked } = event.target;
        setCancellationEmailConsent(checked);
    };

    const togglePatientNotifications = (area: string) => {
        setCollapse((prevCollapse:any) => ({
            ...prevCollapse,
            [area]: !prevCollapse[area]
        }));
    };

    const handleChange = (event: any, type: string) => {
        const { name, value, min, max } = event.target;
        let updatedFieldValidation = { ...fieldValidation };
        if (name in updatedFieldValidation) {
            let fieldKey = name as keyof typeof updatedFieldValidation;
            let isRequired = updatedFieldValidation[fieldKey].required;
            if (value && value.trim() !== '') {
                isRequired = true;
            }
            setCancellationEmailOther(value);
            updatedFieldValidation[fieldKey].valid = validateFieldAndForm(name, type, value, min, max, isRequired);
            setFieldValidation(updatedFieldValidation);
        }
    };

    const validateFieldAndForm = (
        name: string,
        type: string,
        value: string,
        minimum: any = null,
        maximum: any = null,
        required: boolean = true) : boolean => {
        let isFieldValid = validateField(name, type, value, minimum, maximum, required);

        let updatedFieldValidation = { ...fieldValidation };
        if (name in updatedFieldValidation) {
            const fieldKey = name as keyof typeof updatedFieldValidation;

            updatedFieldValidation[fieldKey].valid = isFieldValid;
            setFieldValidation(updatedFieldValidation);
        }

        const validity = Object.keys(updatedFieldValidation).every((field) =>
            updatedFieldValidation[field as keyof typeof updatedFieldValidation].valid === true
        );

        setIsPageValid(validity);

        return isFieldValid;
    };

    const confirmCancellation = async() => {
        let cancelRequest = {
            reasonForCancellation: reasonForCancellation,
            sendCancellationEmailToPatient: cancellationEmailConsent,
            email: cancellationEmailOther ? cancellationEmailOther : patientEmail,
            languageCode: selectedLanguage,
        };

        setIsCanceling(true);
        try {
            const response = await dispatch(rd2RefactorActions.cancelAppointment(previousAppointmentReferenceId, cancelRequest, token));
            if (response.error) {
                setIsCanceling(false);
                setError(schedulingConfig.cancellationFailureMessage || response.error.response.data || 'An error occurred while trying to cancel this appointment.');
            }
            else {
                setIsCanceling(false);
                if (response.payload.data.acknowledge === 0) {
                    setError(schedulingConfig.cancellationFailureMessage);
                }
                else {
                    setIsAppointmentCanceled(true);
                }
            }
        }
        catch (err: any) {
            setIsCanceling(false);
            setError(schedulingConfig.cancellationFailureMessage || 'An error occurred while trying to cancel this appointment.');
        }
    };

    const bookAnotherAppointment = () => {
        if (cancelRescheduleInfo && cancelRescheduleInfo.previousAppointmentReferenceId) {
            dispatch(rd2RefactorActions.clearPreviousAppointment());
        }
        dispatch(rd2RefactorActions.clearAgentInstructions());
        dispatch(rd2RefactorActions.startBookingProcess(
            activeCareOrder,
            decisionSupportOutput,
            availabilitySearchConfig,
            activePatient.details,
            useDecisionSupport,
            routePrefix,
        ));
    };

    const beginNewFlow = () => {
        dispatch(rd2RefactorActions.clearPreviousAppointment());
        dispatch(routes.patientSearch(routePrefix));
    };

    const goBack = () => {
        let actionReferrer = cancelRescheduleInfo.actionReferrer;

        if (actionReferrer === PATIENT_DETAILS) {
            dispatch(rd2RefactorActions.clearPreviousAppointment());
            dispatch(routes.patientDetails(routePrefix, activePatient.id));
        } else if (actionReferrer === APPOINTMENT_DETAILS) {
            dispatch(rd2RefactorActions.clearPreviousAppointment());
            dispatch(routes.appointmentDetails(routePrefix, previousAppointmentReferenceId));
        } else {
            dispatch(rd2RefactorActions.clearPreviousAppointment());
            dispatch(routes.appointmentDetails(routePrefix, previousAppointmentReferenceId));
        }
    };

    const onCancelReasonChange = (e: any) => {
        e.preventDefault();
        setReasonForCancellation(e.target.value);
    };

    const onSelectCancelReason = (e: any) => {
        e.preventDefault();
        let id = parseInt(e.target.value);
        if (id > 0) {
            let selection = systemCancelReasons.find((x:any) => x.id === id);
            setReasonForCancellation(selection.name);
        } else {
            setReasonForCancellation('');
        }
    };

    const resolveConfirmationEmailInstructions = () => {
        let result = 'No email address in patient profile.';
        let isAutomatic = emailCancellationEnabled && !enableConfirmationEmailOptIn;
        let automaticallyText = isAutomatic ? 'automatically ' : '';

        if (patientEmail && patientEmail.trim() !== '') {
            result = `An email confirming cancellation will ${automaticallyText}be sent to ${patientEmail}. Optionally, you may override that email below.`;
        }
        return result;
    };

    let isLoading = isCanceling || isDetailsLoading;
    let cancellationEmailInstructions = resolveConfirmationEmailInstructions();

    return (
        <div className="container-fluid">
            <div>{error && <Alert color="danger">{error}</Alert>}</div>
            {isLoading && (
                <LoadingIndicator
                    loadingMessage={isCanceling ? 'Canceling appointment. Please wait.' : 'Loading. Please wait.'}
                />
            )}
            {!isAppointmentCanceled && !isLoading && (
                <CancelAppointmentDetailsOverview
                    cancellationEmailConsent={cancellationEmailConsent}
                    cancellationEmailInstructions={cancellationEmailInstructions}
                    cancellationEmailOther={cancellationEmailOther}
                    collapse={collapse}
                    confirmCancellation={confirmCancellation}
                    details={appointmentDetails}
                    emailCancellationEnabled={notificationConfig.emailCancellationEnabled}
                    enableCancellationEmailOptIn={enableCancellationEmailOptIn}
                    enableCancellationEmailOther={true}
                    fieldValidation={fieldValidation}
                    goBack={goBack}
                    handleChange={handleChange}
                    handleCheckboxChange={handleCheckboxChange}
                    onCancelReasonChange={onCancelReasonChange}
                    onSelectCancelReason={onSelectCancelReason}
                    patientCancellationEmailDisclaimer={notificationConfig.patientCancellationEmailDisclaimer}
                    patientEmail={patientEmail}
                    previousAppointmentDetails={cancelRescheduleInfo.previousAppointmentDetails}
                    providerFieldConfig={providerFieldConfig}
                    reasonForCancellation={reasonForCancellation}
                    schedulingConfig={schedulingConfig}
                    showInsuranceOnBookAppointment={schedulingConfig.showInsuranceOnBookAppointment}
                    systemCancelReasons={systemCancelReasons}
                    toggle={togglePatientNotifications}
                />
            )}
            {isAppointmentCanceled && (
                <ConfirmationModal
                    className="appointmentCanceledModal"
                    title="Appointment Canceled"
                    buttonLabel="OK"
                    body="Appointment is successfully canceled."
                    isOpen={isAppointmentCanceled}
                    onConfirm={beginNewFlow}
                />
            )}
        </div>
    );
}
export default AppointmentCancelView;